import {
  elementBoundsInWorld,
  LOCATION_CHIP_DEBUG_PAD_PX,
  padBounds,
} from "../../board/BoardModelDebug/geometry";
import {
  type Bounds,
  characterIdsBounds,
  mergeBounds,
} from "../../board/BoardModelDebug/bounds";

/** Union of cast-column location label chips (one site box per run). */
export function locationCastColumnLabelBounds(
  world: HTMLElement,
  locationsRoot: HTMLElement,
  runIds: readonly string[],
): Bounds | null {
  let box: Bounds | null = null;
  for (const runId of runIds) {
    const next = locationRunLabelBounds(world, locationsRoot, runId);
    if (next === null) {
      continue;
    }
    box = mergeBounds(box, next);
  }
  return box;
}

function centerBoxOnX(box: Bounds, centerX: number): Bounds {
  const halfW = (box.maxX - box.minX) / 2;
  return {
    minX: centerX - halfW,
    maxX: centerX + halfW,
    minY: box.minY,
    maxY: box.maxY,
  };
}

function locationCastColumnWithCharacters(
  labelsRoot: HTMLElement,
  characterIds: readonly string[],
  stacks: Bounds | null,
): Bounds | null {
  const chars = characterIdsBounds(labelsRoot, [...characterIds]);
  if (chars === null && stacks === null) {
    return null;
  }
  if (stacks === null) {
    return chars;
  }
  if (chars === null) {
    return stacks;
  }
  const centerX = (chars.minX + chars.maxX) / 2;
  const locsCentered = centerBoxOnX(stacks, centerX);
  return mergeBounds(chars, locsCentered);
}

/** Cast-column location cohort hugging labels, centered on the characters. */
export function locationCastCohortBounds(
  world: HTMLElement,
  labelsRoot: HTMLElement,
  locationsRoot: HTMLElement,
  characterIds: readonly string[],
  locationRunIds: readonly string[],
): Bounds | null {
  const stacks = locationCastColumnLabelBounds(
    world,
    locationsRoot,
    locationRunIds,
  );
  return locationCastColumnWithCharacters(labelsRoot, characterIds, stacks);
}

/** Outer Location frame: union of primary + secondary stacks, then cast. */
export function locationCastColumnGroupBounds(
  world: HTMLElement,
  labelsRoot: HTMLElement,
  locationsRoot: HTMLElement,
  characterIds: readonly string[],
  primaryRunId: string | undefined,
  secondaryRunIds: readonly string[],
): Bounds | null {
  const runIds = [
    ...(primaryRunId === undefined ? [] : [primaryRunId]),
    ...secondaryRunIds,
  ];
  const stacks = locationCastColumnLabelBounds(
    world,
    locationsRoot,
    runIds,
  );
  return locationCastColumnWithCharacters(labelsRoot, characterIds, stacks);
}

export function locationRunLabelBounds(
  world: HTMLElement,
  locationsRoot: HTMLElement,
  runId: string,
): Bounds | null {
  const stack = locationsRoot.querySelector<HTMLElement>(
    `[data-location-run="${CSS.escape(runId)}"]`,
  );
  if (stack === null || stack.style.visibility === "hidden") {
    return null;
  }
  const site = stack.querySelector<HTMLElement>(
    ".timeline-location-box:not(.timeline-location-box--nested)",
  );
  if (site === null || site.hidden || site.style.display === "none") {
    return null;
  }
  return padBounds(
    elementBoundsInWorld(world, site),
    LOCATION_CHIP_DEBUG_PAD_PX,
  );
}
